// game/runstats.ts — the itemised ledger a run keeps about itself (DESIGN §8).
//
// The summary screen is the only place a losing run gets read back to the player,
// so every number it shows has to be accumulated *as it happens* rather than
// reconstructed at the end. This module is that accumulator. It is deliberately
// separate from meta.ts (which owns the banked save) — nothing here is persisted.

import { TUNING } from '../data/tuning'

export type EmberBucket = 'kills' | 'rooms' | 'vault' | 'boss' | 'other'

export interface ZoneRecord {
  zone: number
  name: string
  seconds: number
  cleared: boolean
}

/** Par band for a single zone (DESIGN §3), sourced from tuning. */
export const ZONE_PAR_SECONDS: [number, number] = TUNING.run.zoneParSeconds

export class RunStats {
  zones: ZoneRecord[] = []
  killsByFamily: Record<string, number> = {}
  embers: Record<EmberBucket, number> = { kills: 0, rooms: 0, vault: 0, boss: 0, other: 0 }
  relics: string[] = []
  goldEarned = 0
  goldSpent = 0
  vaultsOpened = 0
  generatorsDown = 0
  /** feat ids advanced this run -> amount, for the "feats advanced" block. */
  featsAdvanced: Record<string, number> = {}

  reset(): void {
    this.zones.length = 0
    for (const k in this.killsByFamily) delete this.killsByFamily[k]
    this.embers.kills = 0
    this.embers.rooms = 0
    this.embers.vault = 0
    this.embers.boss = 0
    this.embers.other = 0
    this.relics.length = 0
    this.goldEarned = 0
    this.goldSpent = 0
    this.vaultsOpened = 0
    this.generatorsDown = 0
    for (const k in this.featsAdvanced) delete this.featsAdvanced[k]
  }

  beginZone(zone: number, name: string): void {
    this.zones.push({ zone, name, seconds: 0, cleared: false })
  }

  /** Called once per zone end with the zone clock; also marks it cleared or not. */
  endZone(seconds: number, cleared: boolean): void {
    const z = this.zones[this.zones.length - 1]
    if (!z) return
    z.seconds = seconds
    z.cleared = cleared
  }

  /** Keeps the in-progress zone's clock live so a death mid-zone still itemises. */
  tickZone(seconds: number): void {
    const z = this.zones[this.zones.length - 1]
    if (z) z.seconds = seconds
  }

  kill(family: string): void {
    this.killsByFamily[family] = (this.killsByFamily[family] ?? 0) + 1
  }

  ember(bucket: EmberBucket, n: number): void {
    if (n <= 0) return
    this.embers[bucket] += n
  }

  feat(track: string, n: number): void {
    this.featsAdvanced[track] = (this.featsAdvanced[track] ?? 0) + n
  }

  get emberTotal(): number {
    return (
      this.embers.kills + this.embers.rooms + this.embers.vault + this.embers.boss + this.embers.other
    )
  }

  get zonesCleared(): number {
    let n = 0
    for (const z of this.zones) if (z.cleared) n++
    return n
  }
}

/** The live ledger. game.ts writes it; ui/summary.ts reads it. */
export const runStats = new RunStats()

/** 'under' | 'in' | 'over' the 4-6 minute par band. */
export function parBand(seconds: number): 'under' | 'in' | 'over' {
  if (seconds < ZONE_PAR_SECONDS[0]) return 'under'
  if (seconds > ZONE_PAR_SECONDS[1]) return 'over'
  return 'in'
}
